import { Compass, Sparkles } from 'lucide-react';
import Logo from '@/components/branding/Logo';

interface MembershipCardProps {
  isPremium: boolean;
  fullName: string;
  memberSince: string | null;
  planLabel: string;
}

export default function MembershipCard({
  isPremium,
  fullName,
  memberSince,
  planLabel,
}: MembershipCardProps) {
  return (
    <div
      className={
        isPremium
          ? 'relative mx-auto w-full max-w-[420px] aspect-[1.586/1] rounded-3xl bg-gradient-to-br from-primary to-primary-dark text-white shadow-2xl border border-white/10 overflow-hidden'
          : 'relative mx-auto w-full max-w-[420px] aspect-[1.586/1] rounded-3xl bg-cream-warm text-text-primary shadow-md border border-border overflow-hidden'
      }
    >
      {isPremium ? (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 80% 0%, rgba(255,255,255,0.18), transparent 60%)',
          }}
        />
      ) : (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 80% 0%, rgba(212,163,115,0.30), transparent 60%)',
          }}
        />
      )}

      <div aria-hidden className="membership-card-shimmer" />

      <div className="relative h-full flex flex-col justify-between p-5 md:p-7 text-left">
        <div className="flex items-start justify-between">
          <Logo
            variant="wordmark"
            tone={isPremium ? 'reverse' : 'default'}
            size="sm"
          />
          <div
            className={
              isPremium
                ? 'flex items-center justify-center w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm'
                : 'flex items-center justify-center w-9 h-9 rounded-full bg-primary/10'
            }
          >
            {isPremium ? (
              <Sparkles size={16} className="text-white" />
            ) : (
              <Compass size={16} className="text-primary" />
            )}
          </div>
        </div>

        <div>
          <div
            className={
              isPremium
                ? 'font-sans text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1.5'
                : 'font-sans text-[10px] uppercase tracking-[0.22em] text-text-muted mb-1.5'
            }
          >
            Cardholder
          </div>
          <div
            className={
              isPremium
                ? 'font-display italic font-light text-2xl md:text-[28px] text-white leading-tight truncate'
                : 'font-display italic font-light text-2xl md:text-[28px] text-text-primary leading-tight truncate'
            }
          >
            {fullName}
          </div>
        </div>

        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <div
              className={
                isPremium
                  ? 'font-sans text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1'
                  : 'font-sans text-[10px] uppercase tracking-[0.22em] text-text-muted mb-1'
              }
            >
              Member since
            </div>
            <div
              className={
                isPremium
                  ? 'font-sans font-semibold text-sm text-white'
                  : 'font-sans font-semibold text-sm text-text-primary'
              }
            >
              {memberSince ?? '—'}
            </div>
          </div>
          <div className="text-right min-w-0">
            <div
              className={
                isPremium
                  ? 'font-sans text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1'
                  : 'font-sans text-[10px] uppercase tracking-[0.22em] text-text-muted mb-1'
              }
            >
              Plan
            </div>
            <div
              className={
                isPremium
                  ? 'font-sans font-semibold text-sm text-white'
                  : 'font-sans font-semibold text-sm text-text-primary'
              }
            >
              {planLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
